import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Filter, Plus, Kanban, List, TrendingUp,
  AlertTriangle, Calendar, X,
} from 'lucide-react'
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor,
  useSensor, useSensors, useDraggable, useDroppable,
  type DragEndEvent, type DragStartEvent,
} from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { api } from '@/lib/api/client'
import type { Opportunity } from '@/types/api'
import { Drawer } from '@/components/primitives/Drawer'

// ── Constants ─────────────────────────────────────────────────────────────────

interface Stage {
  id: string; label: string; weight: number; color: string
}

const STAGES: Stage[] = [
  { id: 'discovery', label: 'Discovery', weight: 0.10, color: 'oklch(0.7 0.04 250)'  },
  { id: 'scoping',   label: 'Scoping',   weight: 0.25, color: 'oklch(0.65 0.13 245)' },
  { id: 'proposal',  label: 'Proposal',  weight: 0.50, color: 'oklch(0.62 0.15 285)' },
  { id: 'sow',       label: 'SoW',       weight: 0.75, color: 'oklch(0.62 0.07 200)' },
  { id: 'won',       label: 'Won',       weight: 1.00, color: 'oklch(0.66 0.13 150)' },
]

const HEALTH_OPTIONS = [
  { value: 'on-track', label: 'On track' },
  { value: 'at-risk',  label: 'At risk'  },
  { value: 'won',      label: 'Won'      },
]

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatKr(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000)     return Math.round(n / 1000) + 'k'
  return String(n)
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 2)   return 'just now'
  if (mins < 60)  return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)   return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days === 1) return 'yesterday'
  return `${days}d ago`
}

function ownerInitials(name: string | null | undefined): string {
  return (name ?? '?').split(' ').filter(Boolean).map(p => p[0]).join('').slice(0, 2).toUpperCase()
}

// ── KpiCard ───────────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string; value: string; unit?: string; sub?: string
  iconRight?: 'alert' | 'trend'; tone?: 'warn' | 'ok'; highlight?: boolean
}

function KpiCard({ label, value, unit, sub, iconRight, tone, highlight }: KpiCardProps) {
  const toneFg = tone === 'warn' ? 'var(--ns-warn)' : tone === 'ok' ? 'var(--ns-ok)' : 'var(--ns-text)'
  const toneBg = tone === 'warn' ? 'var(--ns-warn-soft)' : tone === 'ok' ? 'var(--ns-ok-soft)' : 'var(--ns-surface-2)'
  return (
    <div className="card" style={{
      padding: 14, position: 'relative',
      borderColor:  highlight ? 'var(--ns-brand-strong)' : undefined,
      background:   highlight ? 'linear-gradient(135deg, var(--ns-surface) 0%, var(--ns-brand-soft) 200%)' : undefined,
    }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div className="muted" style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.02em' }}>{label}</div>
        {iconRight && (
          <div style={{ width: 24, height: 24, borderRadius: 6, background: toneBg, color: toneFg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {iconRight === 'alert' ? <AlertTriangle size={13} /> : <TrendingUp size={13} />}
          </div>
        )}
      </div>
      <div className="row" style={{ alignItems: 'baseline', gap: 4, marginTop: 6 }}>
        <span className="mono tabular" style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1 }}>{value}</span>
        {unit && <span className="muted" style={{ fontSize: 12 }}>{unit}</span>}
      </div>
      {sub && <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>{sub}</div>}
    </div>
  )
}

// ── OppCard ───────────────────────────────────────────────────────────────────

function OppCard({ o, stageColor, onClick }: { o: Opportunity; stageColor: string; onClick: () => void }) {
  const isAtRisk = o.health === 'at-risk'
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--ns-surface)', borderRadius: 8, padding: 11,
        border: '1px solid var(--ns-border)', cursor: 'pointer',
        transition: 'border-color 0.12s, box-shadow 0.12s',
        borderLeft: `3px solid ${stageColor}`,
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--ns-border-strong)'; e.currentTarget.style.boxShadow = 'var(--ns-shadow-sm)'; e.currentTarget.style.borderLeftColor = stageColor }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--ns-border)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderLeftColor = stageColor }}
    >
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.3 }}>{o.name}</span>
        {isAtRisk && <span title="At risk" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ns-warn)', flexShrink: 0, marginTop: 5, marginLeft: 6 }} />}
      </div>
      <div className="muted" style={{ fontSize: 11, marginBottom: 8 }}>{o.account}</div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="mono tabular" style={{ fontSize: 13, fontWeight: 600 }}>
          {formatKr(o.value)} <span style={{ color: 'var(--ns-text-subtle)', fontSize: 10.5 }}>kr</span>
        </span>
        <span style={{
          width: 22, height: 22, borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--ns-brand) 0%, var(--ns-brand-strong) 100%)',
          color: 'white', fontSize: 9.5, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{ownerInitials(o.ownerName)}</span>
      </div>
      <div className="row" style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--ns-border)', justifyContent: 'space-between', fontSize: 10.5 }}>
        <span className="muted row" style={{ gap: 4 }}><Calendar size={10} />{o.closeAt?.slice(5) ?? '—'}</span>
        <span className="muted" style={{ color: isAtRisk ? 'var(--ns-warn)' : 'var(--ns-text-muted)' }}>{relativeTime(o.lastActivityAt)}</span>
      </div>
    </div>
  )
}

// ── DraggableOppCard ──────────────────────────────────────────────────────────

function DraggableOppCard({ o, stageColor, onClick }: { o: Opportunity; stageColor: string; onClick: () => void }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: o.id })
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), opacity: isDragging ? 0 : 1, touchAction: 'none' }}
      {...attributes}
      {...listeners}
    >
      <OppCard o={o} stageColor={stageColor} onClick={onClick} />
    </div>
  )
}

// ── DroppableColumn ───────────────────────────────────────────────────────────

function DroppableColumn({ stage, opps, onEdit }: { stage: Stage; opps: Opportunity[]; onEdit: (o: Opportunity) => void }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id })
  const total = opps.reduce((a, b) => a + b.value, 0)
  return (
    <div
      ref={setNodeRef}
      style={{
        background:   isOver ? 'var(--ns-brand-soft)' : 'var(--ns-surface-2)',
        border:       `1px solid ${isOver ? 'var(--ns-brand)' : 'transparent'}`,
        borderRadius: 10, padding: 10, minHeight: 480,
        transition:   'background 0.15s, border-color 0.15s',
      }}
    >
      <div className="row" style={{ marginBottom: 10, justifyContent: 'space-between' }}>
        <div className="row" style={{ gap: 7 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: stage.color }} />
          <span style={{ fontSize: 12, fontWeight: 600 }}>{stage.label}</span>
          <span className="muted tabular" style={{ fontSize: 11 }}>{opps.length}</span>
        </div>
        <span className="mono tabular" style={{ fontSize: 11, color: 'var(--ns-text-muted)' }}>{formatKr(total)} kr</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {opps.map(o => <DraggableOppCard key={o.id} o={o} stageColor={stage.color} onClick={() => onEdit(o)} />)}
        {opps.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', border: '1px dashed var(--ns-border)', borderRadius: 8, color: 'var(--ns-text-subtle)', fontSize: 11.5 }}>
            No deals
          </div>
        )}
      </div>
    </div>
  )
}

// ── KanbanBoard ───────────────────────────────────────────────────────────────

function KanbanBoard({ opps, onEdit }: { opps: Opportunity[]; onEdit: (o: Opportunity) => void }) {
  const qc = useQueryClient()
  const [activeId, setActiveId] = useState<number | null>(null)
  const activeOpp = activeId != null ? (opps.find(o => o.id === activeId) ?? null) : null

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  )

  const stageMutation = useMutation({
    mutationFn: ({ opp, newStage }: { opp: Opportunity; newStage: string }) =>
      api.put<Opportunity>(`/sales/opportunities/${opp.id}`, {
        name:          opp.name,
        account:       opp.account,
        value:         opp.value,
        stage:         newStage,
        closeAt:       opp.closeAt,
        health:        opp.health,
        lastActivity:  opp.lastActivity,
        notes:         opp.notes,
        winLossReason: opp.winLossReason,
        ownerId:       opp.ownerId,
      }),
    onError: () => qc.invalidateQueries({ queryKey: ['opportunities'] }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['opportunities'] }),
  })

  function handleDragStart({ active }: DragStartEvent) {
    setActiveId(active.id as number)
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    setActiveId(null)
    if (!over) return
    const opp      = opps.find(o => o.id === active.id)
    const newStage = over.id as string
    if (!opp || opp.stage === newStage) return
    qc.setQueryData(['opportunities'], (old: Opportunity[] | undefined) =>
      (old ?? []).map(o => o.id === opp.id ? { ...o, stage: newStage } : o),
    )
    stageMutation.mutate({ opp, newStage })
  }

  const cols = STAGES.filter(s => s.id !== 'won')
  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols.length}, 1fr)`, gap: 12 }}>
        {cols.map(s => {
          const colOpps = opps.filter(o => o.stage === s.id)
          return <DroppableColumn key={s.id} stage={s} opps={colOpps} onEdit={onEdit} />
        })}
      </div>
      <DragOverlay>
        {activeOpp && (
          <div style={{ width: 240, opacity: 0.95, transform: 'rotate(1.5deg)', cursor: 'grabbing' }}>
            <OppCard
              o={activeOpp}
              stageColor={STAGES.find(s => s.id === activeOpp.stage)?.color ?? ''}
              onClick={() => {}}
            />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}

// ── OppsTable ─────────────────────────────────────────────────────────────────

function OppsTable({ opps, onEdit }: { opps: Opportunity[]; onEdit: (o: Opportunity) => void }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <table className="table">
        <thead>
          <tr>
            <th>Opportunity</th>
            <th style={{ width: 130 }}>Stage</th>
            <th style={{ width: 120, textAlign: 'right' }}>Value</th>
            <th style={{ width: 110 }}>Close</th>
            <th style={{ width: 90 }}>Owner</th>
            <th style={{ width: 200 }}>Last activity</th>
            <th style={{ width: 80 }}></th>
          </tr>
        </thead>
        <tbody>
          {opps.map(o => {
            const stage = STAGES.find(s => s.id === o.stage) ?? STAGES[0]
            return (
              <tr key={o.id} className="row-clickable" onClick={() => onEdit(o)}>
                <td>
                  <div style={{ fontWeight: 500 }}>{o.name}</div>
                  <div className="muted" style={{ fontSize: 11.5 }}>{o.account}</div>
                </td>
                <td>
                  <span className="row" style={{ gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: stage.color }} />
                    {stage.label}
                  </span>
                </td>
                <td className="mono tabular" style={{ textAlign: 'right', fontWeight: 500 }}>
                  {o.value.toLocaleString('sv-SE')} <span style={{ color: 'var(--ns-text-subtle)', fontSize: 11 }}>kr</span>
                </td>
                <td className="mono tabular" style={{ fontSize: 12 }}>{o.closeAt ?? '—'}</td>
                <td>
                  <span className="row" style={{ gap: 6 }}>
                    <span style={{
                      width: 20, height: 20, borderRadius: '50%',
                      background: 'linear-gradient(135deg, var(--ns-brand) 0%, var(--ns-brand-strong) 100%)',
                      color: 'white', fontSize: 9, fontWeight: 600,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>{ownerInitials(o.ownerName)}</span>
                    <span className="muted" style={{ fontSize: 12 }}>{o.ownerName ?? '—'}</span>
                  </span>
                </td>
                <td>
                  <div style={{ fontSize: 12 }}>{o.lastActivity ?? '—'}</div>
                  <div className="muted tabular" style={{ fontSize: 11 }}>
                    {relativeTime(o.lastActivityAt)} · {o.ageInStage}d in stage
                  </div>
                </td>
                <td>
                  {o.health === 'at-risk' && <span className="badge badge-warn"><span className="dot dot-warn" />At risk</span>}
                  {o.health === 'won'      && <span className="badge badge-ok"><span className="dot dot-ok" />Won</span>}
                </td>
              </tr>
            )
          })}
          {opps.length === 0 && (
            <tr>
              <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 32 }}>No opportunities yet.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

// ── ForecastView ──────────────────────────────────────────────────────────────

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function ForecastView({ opps }: { opps: Opportunity[] }) {
  const open      = opps.filter(o => o.stage !== 'won')
  const byStage   = STAGES.filter(s => s.id !== 'won').map(s => {
    const stageOpps = open.filter(o => o.stage === s.id)
    return {
      ...s,
      total:    stageOpps.reduce((a, b) => a + b.value, 0),
      weighted: stageOpps.reduce((a, b) => a + b.value * s.weight, 0),
      count:    stageOpps.length,
    }
  })
  const totalWeighted = byStage.reduce((a, b) => a + b.weighted, 0)
  const totalRaw      = byStage.reduce((a, b) => a + b.total, 0)
  const maxBar        = Math.max(...byStage.map(s => s.total), 1)

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
      <div className="card" style={{ padding: 18 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Weighted forecast by stage</h3>
        <p className="muted" style={{ margin: '2px 0 18px', fontSize: 11.5 }}>Stage probability × deal value</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {byStage.map(s => (
            <div key={s.id}>
              <div className="row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
                <span className="row" style={{ gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} />
                  <span style={{ fontSize: 12.5, fontWeight: 500 }}>{s.label}</span>
                  <span className="muted tabular" style={{ fontSize: 11 }}>· {s.count} deals · {Math.round(s.weight * 100)}%</span>
                </span>
                <span className="mono tabular" style={{ fontSize: 12, fontWeight: 500 }}>
                  {formatKr(Math.round(s.weighted))} <span className="muted" style={{ fontSize: 10.5 }}>/ {formatKr(s.total)} kr</span>
                </span>
              </div>
              <div style={{ position: 'relative', height: 10, background: 'var(--ns-surface-2)', borderRadius: 5, overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, width: `${(s.total / maxBar) * 100}%`, background: s.color, opacity: 0.18 }} />
                <div style={{ position: 'absolute', inset: 0, width: `${(s.weighted / maxBar) * 100}%`, background: s.color }} />
              </div>
            </div>
          ))}
        </div>
        <div className="row" style={{ marginTop: 22, paddingTop: 16, borderTop: '1px solid var(--ns-border)', justifyContent: 'space-between' }}>
          <div>
            <div className="muted" style={{ fontSize: 11, fontWeight: 500 }}>Weighted forecast</div>
            <div className="mono tabular" style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em' }}>
              {formatKr(Math.round(totalWeighted))} <span style={{ fontSize: 13, color: 'var(--ns-text-muted)' }}>kr</span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="muted" style={{ fontSize: 11, fontWeight: 500 }}>Raw pipeline</div>
            <div className="mono tabular" style={{ fontSize: 16, color: 'var(--ns-text-muted)' }}>{formatKr(totalRaw)} kr</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 18 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Closing this quarter</h3>
        <p className="muted" style={{ margin: '2px 0 14px', fontSize: 11.5 }}>Sorted by close date</p>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {[...open].sort((a, b) => (a.closeAt ?? '').localeCompare(b.closeAt ?? '')).slice(0, 6).map((o, i) => {
            const stage = STAGES.find(s => s.id === o.stage) ?? STAGES[0]
            return (
              <div key={o.id} className="row" style={{ padding: '10px 0', gap: 10, borderTop: i > 0 ? '1px solid var(--ns-border)' : 'none' }}>
                <div style={{ width: 38, textAlign: 'center' }}>
                  <div className="mono tabular" style={{ fontSize: 14, fontWeight: 600, lineHeight: 1 }}>{(o.closeAt ?? '--').slice(8, 10)}</div>
                  <div className="muted" style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>
                    {o.closeAt ? MONTH_NAMES[parseInt(o.closeAt.slice(5, 7)) - 1] : '—'}
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.name}</div>
                  <div className="row" style={{ gap: 6, fontSize: 10.5, marginTop: 2 }}>
                    <span style={{ width: 6, height: 6, borderRadius: 1, background: stage.color }} />
                    <span className="muted">{stage.label}</span>
                    {o.health === 'at-risk' && <span style={{ color: 'var(--ns-warn)' }}>· at risk</span>}
                  </div>
                </div>
                <span className="mono tabular" style={{ fontSize: 12, fontWeight: 500 }}>{formatKr(o.value)} kr</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ── NewOppDrawer ──────────────────────────────────────────────────────────────

function NewOppDrawer({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [name,         setName]         = useState('')
  const [account,      setAccount]      = useState('')
  const [value,        setValue]        = useState('')
  const [stage,        setStage]        = useState('discovery')
  const [closeAt,      setCloseAt]      = useState('')
  const [lastActivity, setLastActivity] = useState('')
  const [notes,        setNotes]        = useState('')

  const createMutation = useMutation({
    mutationFn: (body: object) => api.post<Opportunity>('/sales/opportunities', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['opportunities'] })
      onClose()
    },
  })

  function handleCreate() {
    if (!name.trim() || !account.trim()) return
    createMutation.mutate({
      name:         name.trim(),
      account:      account.trim(),
      value:        parseFloat(value) || 0,
      stage,
      closeAt:      closeAt || null,
      lastActivity: lastActivity.trim() || null,
      notes:        notes.trim() || null,
    })
  }

  return (
    <Drawer open onClose={onClose}>
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>New opportunity</h2>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={15} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Opportunity name <span style={{ color: 'var(--ns-warn)' }}>*</span></span>
            <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Atea — Fabric migration" style={{ fontSize: 13 }} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Company / Account <span style={{ color: 'var(--ns-warn)' }}>*</span></span>
            <input className="input" value={account} onChange={e => setAccount(e.target.value)} placeholder="e.g. Atea Sverige AB" style={{ fontSize: 13 }} />
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 12, fontWeight: 500 }}>Deal value (kr)</span>
              <input type="number" className="input mono" value={value} onChange={e => setValue(e.target.value)} placeholder="0" min="0" step="10000" style={{ fontSize: 13, textAlign: 'right' }} />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 12, fontWeight: 500 }}>Stage</span>
              <select className="input" value={stage} onChange={e => setStage(e.target.value)} style={{ fontSize: 13 }}>
                {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </label>
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Expected close date</span>
            <input type="date" className="input mono" value={closeAt} onChange={e => setCloseAt(e.target.value)} style={{ fontSize: 13 }} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Last activity note</span>
            <input className="input" value={lastActivity} onChange={e => setLastActivity(e.target.value)} placeholder="e.g. Sent SoW draft" style={{ fontSize: 13 }} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Notes</span>
            <textarea className="input" value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Background, context, next steps…" style={{ fontSize: 13, resize: 'vertical' }} />
          </label>
        </div>

        <div className="row" style={{ gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-brand btn-sm"
            onClick={handleCreate}
            disabled={createMutation.isPending || !name.trim() || !account.trim()}
          >
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </Drawer>
  )
}

// ── EditOppDrawer ─────────────────────────────────────────────────────────────

function EditOppDrawer({ opp, onClose }: { opp: Opportunity; onClose: () => void }) {
  const qc = useQueryClient()
  const [name,           setName]           = useState(opp.name)
  const [account,        setAccount]        = useState(opp.account)
  const [value,          setValue]          = useState(String(opp.value))
  const [stage,          setStage]          = useState(opp.stage)
  const [closeAt,        setCloseAt]        = useState(opp.closeAt ?? '')
  const [health,         setHealth]         = useState(opp.health)
  const [lastActivity,   setLastActivity]   = useState(opp.lastActivity ?? '')
  const [notes,          setNotes]          = useState(opp.notes ?? '')
  const [winLossReason,  setWinLossReason]  = useState(opp.winLossReason ?? '')
  const [confirmDelete,  setConfirmDelete]  = useState(false)

  useEffect(() => {
    setName(opp.name); setAccount(opp.account); setValue(String(opp.value))
    setStage(opp.stage); setCloseAt(opp.closeAt ?? ''); setHealth(opp.health)
    setLastActivity(opp.lastActivity ?? ''); setNotes(opp.notes ?? '')
    setWinLossReason(opp.winLossReason ?? '')
  }, [opp.id])

  const updateMutation = useMutation({
    mutationFn: (body: object) => api.put<Opportunity>(`/sales/opportunities/${opp.id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['opportunities'] }); onClose() },
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.delete<unknown>(`/sales/opportunities/${opp.id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['opportunities'] }); onClose() },
  })

  function handleSave() {
    updateMutation.mutate({
      name:          name.trim(),
      account:       account.trim(),
      value:         parseFloat(value) || 0,
      stage,
      closeAt:       closeAt || null,
      health,
      lastActivity:  lastActivity.trim() || null,
      notes:         notes.trim() || null,
      winLossReason: winLossReason.trim() || null,
    })
  }

  const stageColor = STAGES.find(s => s.id === opp.stage)?.color ?? 'var(--ns-border)'

  return (
    <Drawer open onClose={onClose}>
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
          <div>
            <div className="row" style={{ gap: 8, marginBottom: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: stageColor, flexShrink: 0, marginTop: 2 }} />
              <span className="muted" style={{ fontSize: 11 }}>{STAGES.find(s => s.id === opp.stage)?.label ?? opp.stage}</span>
            </div>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{opp.name}</h2>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{opp.account}</div>
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={15} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 1, marginTop: 16, overflowY: 'auto' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Opportunity name</span>
            <input className="input" value={name} onChange={e => setName(e.target.value)} style={{ fontSize: 13 }} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Company / Account</span>
            <input className="input" value={account} onChange={e => setAccount(e.target.value)} style={{ fontSize: 13 }} />
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 12, fontWeight: 500 }}>Deal value (kr)</span>
              <input type="number" className="input mono" value={value} onChange={e => setValue(e.target.value)} min="0" step="10000" style={{ fontSize: 13, textAlign: 'right' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 12, fontWeight: 500 }}>Stage</span>
              <select className="input" value={stage} onChange={e => setStage(e.target.value)} style={{ fontSize: 13 }}>
                {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </label>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 12, fontWeight: 500 }}>Close date</span>
              <input type="date" className="input mono" value={closeAt} onChange={e => setCloseAt(e.target.value)} style={{ fontSize: 13 }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 12, fontWeight: 500 }}>Health</span>
              <select className="input" value={health} onChange={e => setHealth(e.target.value as Opportunity['health'])} style={{ fontSize: 13 }}>
                {HEALTH_OPTIONS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
              </select>
            </label>
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Last activity note</span>
            <input className="input" value={lastActivity} onChange={e => setLastActivity(e.target.value)} placeholder="e.g. Sent proposal" style={{ fontSize: 13 }} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Notes</span>
            <textarea className="input" value={notes} onChange={e => setNotes(e.target.value)} rows={3} style={{ fontSize: 13, resize: 'vertical' }} />
          </label>

          {(stage === 'won' || health === 'won') && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 12, fontWeight: 500 }}>Win / loss reason</span>
              <textarea className="input" value={winLossReason} onChange={e => setWinLossReason(e.target.value)} rows={2} style={{ fontSize: 13, resize: 'vertical' }} />
            </label>
          )}

          <div className="muted" style={{ fontSize: 11, paddingTop: 4 }}>
            {opp.ageInStage}d in current stage · created {relativeTime(opp.createdAt)}
          </div>
        </div>

        <div style={{ marginTop: 20 }}>
          {confirmDelete ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, background: 'var(--ns-warn-soft)', borderRadius: 8, border: '1px solid var(--ns-warn-border, var(--ns-warn))' }}>
              <span style={{ fontSize: 12, fontWeight: 500 }}>Delete this opportunity?</span>
              <div className="row" style={{ gap: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(false)}>Cancel</button>
                <button className="btn btn-sm" style={{ background: 'var(--ns-warn)', color: 'white', border: 'none' }} onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}>
                  {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          ) : (
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--ns-warn)' }} onClick={() => setConfirmDelete(true)}>Delete</button>
              <div className="row" style={{ gap: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
                <button className="btn btn-brand btn-sm" onClick={handleSave} disabled={updateMutation.isPending || !name.trim() || !account.trim()}>
                  {updateMutation.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  )
}

// ── SalesPage ─────────────────────────────────────────────────────────────────

export default function SalesPage() {
  const [view,       setView]       = useState<'board' | 'table' | 'forecast'>('board')
  const [scope,      setScope]      = useState<'mine' | 'team'>('team')
  const [newOppOpen, setNewOppOpen] = useState(false)
  const [editOpp,    setEditOpp]    = useState<Opportunity | null>(null)

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn:  () => api.get<{ userId: number | null }>('/me'),
  })

  const { data: allOpps = [], isLoading, isError } = useQuery({
    queryKey: ['opportunities'],
    queryFn:  () => api.get<Opportunity[]>('/sales/opportunities'),
  })

  const opps = scope === 'mine'
    ? allOpps.filter(o => me?.userId != null && o.ownerId === me.userId)
    : allOpps

  const openOpps      = opps.filter(o => o.stage !== 'won')
  const totalValue    = openOpps.reduce((a, b) => a + b.value, 0)
  const weightedValue = openOpps.reduce((a, o) => a + o.value * (STAGES.find(s => s.id === o.stage)?.weight ?? 0), 0)
  const wonMtd        = opps.filter(o => o.stage === 'won').reduce((a, b) => a + b.value, 0)
  const atRiskCount   = opps.filter(o => o.health === 'at-risk').length

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Sales</div>
          <h1 className="page-title">Pipeline</h1>
          <p className="page-subtitle">Track opportunities, forecast the quarter, and follow up on stalled deals.</p>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn-ghost btn-sm"><Filter size={13} />Filters</button>
          <button className="btn btn-brand btn-sm" onClick={() => setNewOppOpen(true)}><Plus size={13} />New opportunity</button>
        </div>
      </div>

      <div className="row" style={{ marginBottom: 18, gap: 12, justifyContent: 'space-between' }}>
        <div className="row" style={{ gap: 4, padding: 3, background: 'var(--ns-surface-2)', border: '1px solid var(--ns-border)', borderRadius: 8 }}>
          {([['mine', 'My pipeline'], ['team', 'Team']] as const).map(([id, label]) => (
            <button key={id} onClick={() => setScope(id)} style={{
              padding: '5px 12px', borderRadius: 5, border: 'none',
              background: scope === id ? 'var(--ns-surface)' : 'transparent',
              boxShadow: scope === id ? 'var(--ns-shadow-sm)' : 'none',
              fontSize: 12, fontWeight: 500, cursor: 'pointer',
              color: scope === id ? 'var(--ns-text)' : 'var(--ns-text-muted)',
            }}>{label}</button>
          ))}
        </div>
        <div className="row" style={{ gap: 4, padding: 3, background: 'var(--ns-surface-2)', border: '1px solid var(--ns-border)', borderRadius: 8 }}>
          {([
            ['board',    'Board',    <Kanban size={12} />    ],
            ['table',    'Table',    <List size={12} />      ],
            ['forecast', 'Forecast', <TrendingUp size={12} />],
          ] as const).map(([id, label, icon]) => (
            <button key={id} onClick={() => setView(id)} style={{
              padding: '5px 10px', borderRadius: 5, border: 'none',
              background: view === id ? 'var(--ns-surface)' : 'transparent',
              boxShadow: view === id ? 'var(--ns-shadow-sm)' : 'none',
              fontSize: 12, fontWeight: 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
              color: view === id ? 'var(--ns-text)' : 'var(--ns-text-muted)',
            }}>
              {icon}{label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <KpiCard label="Open pipeline"     value={formatKr(totalValue)}            unit="kr" sub={`${openOpps.length} active opportunities`} />
        <KpiCard label="Weighted forecast" value={formatKr(Math.round(weightedValue))} unit="kr" sub="Based on stage probability" highlight />
        <KpiCard label="Won MTD"           value={formatKr(wonMtd)}                unit="kr" sub="Closed-won this month" />
        <KpiCard label="At-risk deals"     value={String(atRiskCount)}             sub="No activity 7d+" iconRight="alert" tone={atRiskCount > 0 ? 'warn' : undefined} />
      </div>

      {isLoading ? (
        <div className="muted" style={{ textAlign: 'center', paddingTop: 48, fontSize: 13 }}>Loading…</div>
      ) : isError ? (
        <div className="muted" style={{ textAlign: 'center', paddingTop: 48, fontSize: 13 }}>Failed to load pipeline — check your connection and try refreshing.</div>
      ) : (
        <>
          {view === 'board'    && <KanbanBoard opps={opps} onEdit={setEditOpp} />}
          {view === 'table'    && <OppsTable   opps={opps} onEdit={setEditOpp} />}
          {view === 'forecast' && <ForecastView opps={opps} />}
        </>
      )}

      {newOppOpen && <NewOppDrawer onClose={() => setNewOppOpen(false)} />}
      {editOpp    && <EditOppDrawer opp={editOpp} onClose={() => setEditOpp(null)} />}
    </div>
  )
}
